import TableContainer from "components/Common/TableContainer";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import React, { useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import CountUp from "react-countup";
import { formatDateStringToKorea, formatISOString } from "helpers/format";
import {
  COLOR_STATUS_CODE,
  COLOR_STATUS_COURSE,
  LIST_LANGUAGE_OPTION,
  LIST_STATUS_CODE_ACTIVE_OPTION,
  LIST_STATUS_COURSE_OPTION,
} from "helpers/constans";
import CopyWrapper from "components/Common/CopyWrapper";
import { truncate } from "utils";
import CollapseFilter from "components/Common/CollapseFilter";
import LabelWrapper from "components/Common/LabelWrapper";
import DropdownAsyncAgency from "components/Common/DropdownAsyncAgency";
import DatePickerCustom from "components/Common/DatePickerCustom";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import DropdownAsyncCourse from "components/Common/DropdownAsyncCourse";
import { getCourseUser } from "store/thunks";

registerLocale("en", en);
registerLocale("ko", ko);

export interface IProps {
  userId?: any;
}

interface Option {
  label: string;
  value: string;
}

const TYPE_SELECT_DEFAULT: string = "created_at";

export interface ISearch {
  page: number;
  limit: number;
  keyword: string;
}

const DEFAULT_DATA_SEARCH = {
  page: 1,
  limit: 30,
  keyword: "",
};

const CourseList = ({ userId }: IProps) => {
  const { t, i18n } = useTranslation();

  const [prepaymentCourses, setPrepaymentCourses] = useState<any>([]);
  const [agencySearch, setAgencySearch] = useState<Option | any>("");
  const [keyword, setKeyword] = useState<Option | any>("");
  const [statusSearch, setStatusSearch] = useState<any>("");
  const [selectedAgency, setSelectedAgency] = useState<any>({
    label: t("All"),
    value: "all",
  });
  const [courseSearch, setCourseSearch] = useState<Option | any>({
    name: t("All Filter"),
    value: "all",
    id: "all",
  });

  const [dateSearch, setDateSearch] = useState<any[]>([null, null]);
  const [startDate, endDate] = dateSearch;

  const handleChangePicker = (values: any[] = []) => {
    setDateSearch((_prev: any) => values);
  };

  const searchData = () => {
    const params = {
      keyword,
      "user_ids[]": userId,
      "course_ids[]": courseSearch?.value === "all" ? "" : courseSearch?.value,
      course_status: statusSearch,
      paid_from: startDate ? formatISOString(startDate, "start") : "",
      paid_to: endDate ? formatISOString(endDate, "end") : "",
      "agency_codes[]": agencySearch === "all" ? "" : agencySearch,
      sort_by: TYPE_SELECT_DEFAULT,
      page: 1,
      time_request: +new Date(),
    };
    getData(params);
  };

  const resetData = () => {
    const params = {
      keyword: "",
      "user_ids[]": userId,
      "agency_codes[]": "",
      "course_ids[]": "",
      paid_from: "",
      paid_to: "",
      course_status: "",
      sort_by: TYPE_SELECT_DEFAULT,
      time_request: +new Date(),
    };
    setStatusSearch("");
    setKeyword("");
    setAgencySearch("");
    setCourseSearch({
      name: t("All Filter"),
      value: "all",
      id: "all",
    });
    setSelectedAgency({
      label: t("All"),
      value: "all",
    });
    setDateSearch([null, null]);
    getData(params);
  };

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 80,
        thClass: "whitespace-nowrap",
        Cell: ({ ...props }) => {
          return <div>{prepaymentCourses?.length - props?.row?.index}</div>;
        },
      },
      {
        Header: t("Status"),
        accessor: "status",
        thWidth: 120,
        thClass: "text-center whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            <div className="text-center" style={{ minWidth: 80 }}>
              <span
                className={`rounded-pill badge bg-${
                  COLOR_STATUS_CODE[cell?.value] || "secondary"
                }`}
              >
                {t(
                  LIST_STATUS_CODE_ACTIVE_OPTION?.find(
                    (item: any) => item?.value === cell?.value
                  )?.label || ""
                )}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("percent_learn"),
        accessor: "learningPercent",
        filterable: true,
        sortable: false,
        thWidth: 250,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            <>
              <div className="cursor-pointer">{cell?.value}%</div>
            </>
          );
        },
      },
      // {
      //   Header: t("Evaluation status"),
      //   accessor: "evaluation_status",
      //   filterable: true,
      //   sortable: false,
      //   thClass: "text-end whitespace-nowrap",
      //   Cell: (cell: any) => {
      //     return (
      //       <>
      //         <div className="cursor-pointer text-end">{cell?.value}</div>
      //       </>
      //     );
      //   },
      // },
      {
        Header: t("Course Name"),
        accessor: "course.name",
        filterable: true,
        sortable: false,
        thWidth: 250,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            <>
              <div className="cursor-pointer">{cell?.value}</div>
            </>
          );
        },
      },
      {
        Header: t("thumbnail"),
        accessor: "course.thumbnail",
        filterable: true,
        sortable: false,
        thWidth: 250,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            <div>
              <img
                src={cell?.value}
                alt="thumbnail"
                style={{ height: "50px" }}
              />
            </div>
          );
        },
      },
      {
        Header: t("Category"),
        accessor: "course.category.name",
        filterable: true,
        sortable: false,
        thWidth: 250,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            <>
              <div className="cursor-pointer">{cell?.value}</div>
            </>
          );
        },
      },
      {
        Header: t("Language"),
        accessor: "course.lang",
        filterable: true,
        sortable: false,
        thWidth: 250,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            <>
              <div className="cursor-pointer">
                {
                  LIST_LANGUAGE_OPTION.find(
                    (item) => item?.value === cell?.value
                  )?.label
                }
              </div>
            </>
          );
        },
      },
      {
        Header: t("chapter"),
        accessor: "course.total_chapters",
        filterable: true,
        sortable: false,
        thWidth: 250,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            <>
              <div className="cursor-pointer">{cell?.value}</div>
            </>
          );
        },
      },
      {
        Header: t("lecture"),
        accessor: "course.total_lectures",
        filterable: true,
        sortable: false,
        thWidth: 250,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            <>
              <div className="cursor-pointer">{cell?.value}</div>
            </>
          );
        },
      },
      {
        Header: t("purchase_date"),
        accessor: "paid_at",
        filterable: true,
        sortable: false,
        thWidth: 250,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="" style={{ minWidth: "120px" }}>
              {cell?.value ? (
                <span>
                  <span>{arrDate?.[0] || ""}</span> <br />{" "}
                  <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                </span>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        Header: t("expiration_date"),
        accessor: "expired_at",
        filterable: true,
        sortable: false,
        thWidth: 250,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="" style={{ minWidth: "120px" }}>
              {cell?.value ? (
                <span>
                  <span>{arrDate?.[0] || ""}</span> <br />{" "}
                  <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
                </span>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        Header: t("Redeem Code"),
        accessor: "redeem_code",
        filterable: true,
        thWidth: 180,
        thClass: "whitespace-nowrap",
        sortable: false,
        Cell: (cell: any) => {
          const redeemCode: any = cell?.row?.original?.redeem_code;

          return (
            redeemCode && (
              <>
                <CopyWrapper
                  contentCopy={redeemCode}
                  style={{ minWidth: "70px" }}
                >
                  <span className="text-primary">
                    {truncate(redeemCode, 70)}
                  </span>
                </CopyWrapper>
              </>
            )
          );
        },
      },

      {
        Header: t("Agency"),
        accessor: "agency.name",
        filterable: true,
        sortable: false,
        thWidth: 250,
        thClass: "whitespace-nowrap",
        Cell: (cell: any) => {
          return (
            <>
              <div className="cursor-pointer text-nowrap">{cell?.value}</div>
            </>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n?.language, prepaymentCourses?.length]
  );

  const getData = async (params: any) => {
    try {
      const resDetail: any = await getCourseUser(params);
      if (resDetail?.data) {
        setPrepaymentCourses(resDetail?.data);
      }
    } catch (error: any) {
      return error;
    }
  };

  useEffect(() => {
    if (userId) {
      const params = {
        "user_ids[]": userId,
      };
      getData(params);
    }
  }, [userId]);

  // End::Delete
  useEffect(() => {
    document.title = `${t("Courses")} - ${t(
      "Users Management"
    )} | Picaverse Agency Star+`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <div className="mt-3">
        <Container fluid>
          <Row>
            <Col lg={12}>
              <Card
                id="customerList"
                style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              >
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-sm">
                      <div>
                        <h5 className="card-title mb-0">
                          <span className="me-2">{t("Total")}:</span>
                          <CountUp
                            start={0}
                            end={prepaymentCourses?.length || 0}
                            duration={1}
                            className="text-primary"
                          />
                        </h5>
                      </div>
                    </div>
                  </Row>
                </CardHeader>
                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4">
                  <CollapseFilter>
                    <Row className="g-4 align-items-center mt-0 mt-md-2">
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper
                          label={`${t("Keyword")}`}
                          isShow={!!keyword}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Keyword")}...`}
                            value={keyword}
                            onChange={(e) => setKeyword(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Agency")} isShow={true}>
                          <DropdownAsyncAgency
                            name="agency"
                            onChangeSelect={(event: any) => {
                              setAgencySearch(event?.data?.code);
                              setSelectedAgency(event);
                            }}
                            isMulti={false}
                            placeholder={`${t("Select Agency")}...`}
                            isShowIconCheck={false}
                            listCheckedUsers={selectedAgency}
                            isAllOption={true}
                            isNotBelongToAgency={false}
                            isClearable={false}
                          />
                        </LabelWrapper>
                      </Col>
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Course")} isShow={true}>
                          <DropdownAsyncCourse
                            name={`course_id`}
                            onChangeSelect={(event: any) => {
                              console.log(event?.data);
                              if (event?.value === "all") {
                                setCourseSearch({
                                  id: "all",
                                  name: "All",
                                  value: "all",
                                  label: "All",
                                });
                              } else {
                                setCourseSearch({
                                  ...event?.data,
                                  value: event?.value,
                                });
                              }
                            }}
                            isMulti={false}
                            placeholder={`${t("Select Course")}...`}
                            isShowIconCheck={false}
                            isAllOption={true}
                            listCheckedCourse={courseSearch}
                            isClearable={false}
                          />
                        </LabelWrapper>
                      </Col>
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3"
                      >
                        <LabelWrapper label={t("Status")} isShow={true}>
                          <DropdownStatusNew
                            name="method"
                            dataList={LIST_STATUS_COURSE_OPTION || []}
                            placeholder={`${t("No data")}`}
                            className="dropdown-status-rounded"
                            classNamePrefix="name-prefix"
                            initialValue={statusSearch}
                            onChangeSelect={(event: any) =>
                              setStatusSearch(event?.value)
                            }
                            colors={COLOR_STATUS_COURSE}
                            isHasOptionAll={true}
                            optionAll={{
                              label: t("All Filter"),
                              value: "",
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-3 date-picker-wrapper-custom"
                      >
                        <LabelWrapper
                          label={t("Date Range")}
                          isShow={!!startDate || !!endDate}
                        >
                          <DatePickerCustom
                            startDate={startDate || null}
                            endDate={endDate || null}
                            onChangePicker={handleChangePicker}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={12}
                        xl={12}
                        xxl={12}
                        className="hstack gap-1 justify-content-center justify-content-md-end mt-sm-3 mt-md-3"
                      >
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-1"
                            onClick={searchData}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t("Button Search")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t("Button Reset")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </CollapseFilter>
                </div>
                <CardBody className="pt-3">
                  <div>
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={prepaymentCourses}
                      totalRecords={prepaymentCourses?.length}
                      manualSorting={true}
                      isShowPagination={false}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CourseList;
